import { useState, useRef, useEffect, Fragment } from "react";
import apiClient from "../../context/apiClient";
import {
  Dialog,
  Box,
  Typography,
  Avatar,
  Paper,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Divider,
  OutlinedInput,
  Tooltip,
  Grow,
  keyframes,
} from "@mui/material";

import { Close } from "@mui/icons-material";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import SendIcon from "@mui/icons-material/Send";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { useChatBot } from "../../context/ChatBotContext";

// Modern typing indicator
const bounce = keyframes`
  0%, 80%, 100% { transform: scale(0); }
  40% { transform: scale(1.0); }
`;

const TypingIndicator = () => (
  <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 2 }}>
    <Paper
      elevation={2}
      sx={{
        p: "12px 16px",
        borderRadius: 2,
        bgcolor: "background.paper",
        display: "flex",
        alignItems: "center",
      }}
    >
      <Box sx={{ display: "flex", gap: "4px" }}>
        <Box
          sx={{
            animation: `${bounce} 1.4s infinite ease-in-out both`,
            animationDelay: "0s",
            bgcolor: "grey.400",
            width: 8,
            height: 8,
            borderRadius: "50%",
          }}
        />
        <Box
          sx={{
            animation: `${bounce} 1.4s infinite ease-in-out both`,
            animationDelay: "0.2s",
            bgcolor: "grey.400",
            width: 8,
            height: 8,
            borderRadius: "50%",
          }}
        />
        <Box
          sx={{
            animation: `${bounce} 1.4s infinite ease-in-out both`,
            animationDelay: "0.4s",
            bgcolor: "grey.400",
            width: 8,
            height: 8,
            borderRadius: "50%",
          }}
        />
      </Box>
    </Paper>
  </Box>
);

const ChatBot = () => {
  const { isOpen, setIsOpen, language, setLanguage } = useChatBot();

  const [inputMessage, setInputMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([
        {
          id: "initial",
          message:
            "Hello! I'm your AI Career Assistant. How can I help you today?",
          type: "bot",
          timestamp: new Date(),
        },
      ]);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
    }
  }, [messages, isLoading, isOpen]);

  const handleSendMessage = async (query) => {
    if (!query || isLoading) return;

    const userMessage = {
      id: Date.now(),
      message: query,
      type: "user",
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setInputMessage("");
    setIsLoading(true);

    try {
      const langName = language.charAt(0).toUpperCase() + language.slice(1);
      const payload = { query, language: langName };

      const response = await apiClient.post("/chatbot/query/", payload);

      const botMessage = {
        id: Date.now() + 1,
        message: response.data.response,
        type: "bot",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, botMessage]);
    } catch (error) {
      console.error("API Error:", error);
      const errorMessage = {
        id: Date.now() + 1,
        message:
          "Sorry, I'm having trouble connecting. Please try again later.",
        type: "bot",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(inputMessage.trim());
    }
  };

  const languages = [
    { code: "english", name: "English" },
    { code: "hindi", name: "हिंदी" },
    { code: "tamil", name: "தமிழ்" },
    { code: "kannada", name: "ಕನ್ನಡ" },
    { code: "marathi", name: "मराठी" },
    { code: "telugu", name: "తెలుగు" },
  ];

  const quickActions = [
    "Review my resume",
    "Practice interview",
    "Find jobs",
    "Skill recommendations",
    "Salary insights",
    "Career roadmap",
  ];

  const tooltipContent = (
    <Fragment>
      <Typography fontWeight="bold" fontSize="1rem" mb={1}>
        Need help? 👋
      </Typography>
      <Typography variant="body2">
        Click to open the AI Career Assistant.
      </Typography>
    </Fragment>
  );

  return (
    <>
      {!isOpen && (
        <Tooltip
          title={tooltipContent}
          placement="left"
          arrow
          TransitionComponent={Grow}
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: "rgb(30, 41, 59)",
                maxWidth: 320,
                padding: "1rem",
              },
            },
            arrow: { sx: { color: "rgb(30, 41, 59)" } },
          }}
        >
          <IconButton
            onClick={() => setIsOpen(true)}
            sx={{
              position: "fixed",
              bottom: 32,
              right: 32,
              width: 64,
              height: 64,
              color: "#0D0D0D",
              background: "linear-gradient(45deg, #D4AF37 30%, #F5D76E 90%)",
              boxShadow: "0 4px 15px rgba(212, 175, 55, 0.3)",
              transition: "transform 0.2s ease-in-out",
              "&:hover": {
                transform: "scale(1.1)",
                background: "linear-gradient(45deg, #F5D76E 30%, #D4AF37 90%)",
                boxShadow: "0 6px 20px rgba(212, 175, 55, 0.5)",
              },
              zIndex: 1200,
            }}
          >
            <SmartToyOutlinedIcon sx={{ fontSize: 32 }} />
          </IconButton>
        </Tooltip>
      )}

      <Dialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            position: "fixed",
            bottom: 32,
            right: 32,
            m: 0,
            width: 380,
            maxHeight: 600,
            borderRadius: 4,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            boxShadow:
              "0 10px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)",
          },
        }}
      >
        <Box
          sx={{
            p: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            color: "#0D0D0D",
            background: "linear-gradient(45deg, #D4AF37 30%, #F5D76E 90%)",
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar
              sx={{
                bgcolor: "rgba(13,13,13,0.1)",
                color: "#0D0D0D",
                width: 40,
                height: 40,
              }}
            >
              <SmartToyOutlinedIcon fontSize="small" />
            </Avatar>
            <Box>
              <Typography variant="h6" fontSize="1rem" fontWeight="bold">
                AI Career Assistant
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                Online
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <FormControl variant="outlined" size="small" sx={{ minWidth: 100 }}>
              <InputLabel
                id="language-select-label"
                sx={{
                  color: "rgba(13,13,13,0.7)",
                  "&.Mui-focused": { color: "#0D0D0D" },
                }}
              >
                Language
              </InputLabel>
              <Select
                labelId="language-select-label"
                label="Language"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                IconComponent={ExpandMoreIcon}
                sx={{
                  color: "#0D0D0D",
                  ".MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(13, 13, 13, 0.5)",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#0D0D0D",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#0D0D0D",
                  },
                  ".MuiSvgIcon-root": { color: "#0D0D0D" },
                }}
              >
                {languages.map((lang) => (
                  <MenuItem key={lang.code} value={lang.code}>
                    {lang.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <IconButton
              onClick={() => setIsOpen(false)}
              sx={{ color: "#0D0D0D" }}
            >
              <Close />
            </IconButton>
          </Box>
        </Box>

        <Box
          sx={{
            flexGrow: 1,
            p: 2,
            display: "flex",
            flexDirection: "column",
            overflowY: "auto",
            bgcolor: "grey.50",
          }}
        >
          {messages.map((message) => (
            <Grow in={true} timeout={500} key={message.id}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent:
                    message.type === "user" ? "flex-end" : "flex-start",
                  mb: 2,
                  flexShrink: 0,
                }}
              >
                <Paper
                  elevation={2}
                  sx={{
                    p: 1.5,
                    maxWidth: "90%",
                    borderRadius: 3,
                    bgcolor:
                      message.type === "user" ? "#D4AF37" : "background.paper",
                    color: message.type === "user" ? "#0D0D0D" : "text.primary",
                    borderTopLeftRadius: message.type === "bot" ? 4 : 24,
                    borderTopRightRadius: message.type === "user" ? 4 : 24,
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                  >
                    {message.message}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      opacity: 0.8,
                      display: "block",
                      mt: 0.5,
                      textAlign: "right",
                    }}
                  >
                    {message.timestamp.toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </Typography>
                </Paper>
              </Box>
            </Grow>
          ))}
          {isLoading && <TypingIndicator />}
          <div ref={messagesEndRef} />
        </Box>

        <Box
          sx={{
            bgcolor: "background.paper",
            p: 1.5,
            borderTop: 1,
            borderColor: "divider",
            flexShrink: 0,
          }}
        >
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mb: 1, display: "block", px: 1 }}
            >
              Quick Actions:
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, px: 1 }}>
              {quickActions.map((action) => (
                <Chip
                  key={action}
                  label={action}
                  clickable
                  onClick={() => handleSendMessage(action)}
                  sx={{
                    bgcolor: "rgba(212, 175, 55, 0.1)",
                    color: "#B8860B",
                    border: "1px solid rgba(212, 175, 55, 0.3)",
                    fontWeight: 500,
                    "&:hover": { bgcolor: "rgba(212, 175, 55, 0.2)" },
                  }}
                />
              ))}
            </Box>
          </Box>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <OutlinedInput
              fullWidth
              placeholder="Type your message..."
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isLoading}
              multiline
              maxRows={4}
              sx={{
                borderRadius: "20px",
                bgcolor: "grey.100",
                "& .MuiOutlinedInput-notchedOutline": { border: "none" },
              }}
            />
            <IconButton
              onClick={() => handleSendMessage(inputMessage.trim())}
              disabled={!inputMessage.trim() || isLoading}
              sx={{
                background: "linear-gradient(45deg, #D4AF37 30%, #F5D76E 90%)",
                color: "#0D0D0D",
                borderRadius: 2,
                "&:hover": {
                  background:
                    "linear-gradient(45deg, #F5D76E 30%, #D4AF37 90%)",
                },
                "&.Mui-disabled": { background: "grey.300", color: "grey.500" },
              }}
            >
              <SendIcon sx={{ transform: "rotate(-45deg)" }} />
            </IconButton>
          </Box>
        </Box>
      </Dialog>
    </>
  );
};

export default ChatBot;
